import dayjs from "dayjs"
import React, { useMemo } from "react"
import { GoVerified } from "react-icons/go"

import { ShareIcon, TabTag } from "@/components/shared"
import { Image, Skeleton } from "@/components/ui"
import { ECampaignStatus, ECampaignType, EFolderAssets } from "@/modules/helpers/enum"
import { useShowCampaign } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendAddBadgeCount, sendShare } from "@/modules/helpers/utils"
import type { AffiliateCampaign, Campaign } from "@/modules/types"

interface CampaignItemProps {
  data?: Campaign | AffiliateCampaign
}

const CampaignItem: React.FC<CampaignItemProps> = ({ data }) => {
  const { setId } = useShowCampaign()
  const [details, folderAssets] = useMemo(() => {
    if (data?.type === ECampaignType.REVIEW_PRODUCT) {
      return [data, EFolderAssets.THUMBNAIL_CAMPAIGN]
    }
    if (data?.type === ECampaignType.COST_PER_VIEW) {
      return [data, EFolderAssets.THUMBNAIL_CAMPAIGN]
    }
    return [data, EFolderAssets.THUMBNAIL_AFFILIATE_CAMPAIGN]
  }, [data])
  const type = data?.type || ECampaignType.REVIEW_PRODUCT

  const navigatePath = () => {
    switch (data?.type) {
      case ECampaignType.COST_PER_VIEW:
        return `/campaign/cpv/${data?.id}`
      case ECampaignType.AFFILIATE:
        return `/campaign/affiliate/${data?.id}`
      default:
        return `/campaign/review/${data?.id}`
    }
  }

  const handleShare = () => {
    sendShare({
      url: `${import.meta.env.VITE_APP_URL_DOMAIN}${navigatePath()}`,
      title: data?.title || ""
    })
    sendAddBadgeCount(1)
  }

  return (
    <div
      onClick={() => {
        setId(data?.id || "")
      }}
      className="flex flex-col overflow-hidden cursor-pointer justify-between h-full rounded-md ease-in duration-100"
      id={data?.id}
    >
      <div className="relative">
        <div className="w-full rounded-2xl aspect-square border border-gray-200 overflow-hidden relative">
          <Image
            src={
              details?.thumbnailName?.includes("https")
                ? details?.thumbnailName
                : getImageByNameFromS3(details?.thumbnailName, details?.createdBy, folderAssets)
            }
            alt=""
            className="w-full aspect-square rounded-lg shadow-sm group-hover:scale-110 object-cover transition-all duration-200 ease-linear"
          />
          <div className="rounded-tr-[16px] rounded-bl-[10px] border-l-[0.5px] border-b-[0.5px] border-neutral-200 bg-gradient-primary absolute bottom-0  px-3 flex justify-center items-center gap-1 py-1.5 text-white max-w-[80%]">
            <GoVerified size={12} color="#fff" />
            <span className="text-xss font-light overflow-ellipsis line-clamp-1">
              {details?.shopName || "Nhãn hàng"}
            </span>
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation()
              handleShare()
            }}
            className="bg-gradient-primary rounded-full absolute top-2 right-2 shadow active:scale-95 transition-all duration-200 ease-linear w-7 h-7 flex items-center justify-center"
          >
            <ShareIcon />
          </div>
        </div>
        <div className="flex flex-col gap-1 p-2">
          <div className="flex gap-1 flex-wrap">
            {!!(data as AffiliateCampaign)?.cashAmount && (
              <TabTag label="Cast camp" isTextGradient />
            )}
            {/* <TabTag label="Zxu" isTextGradient /> */}
            <TabTag label="Mẫu miễn phí" />
            {(data as AffiliateCampaign)?.isSelfFaceRequired && <TabTag label="Cần show mặt" />}
          </div>

          {!!(data as AffiliateCampaign)?.cashAmount && (
            <span className="text-gradient-primary text-sm font-semibold">
              Cast: {Number((data as AffiliateCampaign)?.cashAmount)?.toLocaleString()}đ /KOC
            </span>
          )}

          <h2 className="text-sm font-medium line-clamp-2 h-11">{details?.title}</h2>
        </div>
      </div>

      {type === ECampaignType.REVIEW_PRODUCT && (
        <div className="flex p-1 w-full justify-center">
          <div className="mt-auto w-full text-[11px] lg:text-xs text-blue-500 rounded-md p-2 mb-2 bg-blue-500/10 font-semibold flex items-center gap-2">
            <span>
              Cát xê:{" "}
              {Number((details as Campaign)?.pricePerKol || 0)?.toLocaleString("de-DE") || 0} VND /
              người
            </span>
          </div>
        </div>
      )}

      {type === ECampaignType.REVIEW_PRODUCT && data?.status !== ECampaignStatus.FINISHED && (
        <div className="flex p-1 w-full justify-center">
          <div className="bg-black text-[11px] lg:text-xs text-white p-2 rounded-md font-semibold flex items-center gap-2">
            <span className="text-center">
              Hạn cuối đăng ký:{" "}
              {(details as Campaign)?.startAt &&
                dayjs((details as Campaign)?.startAt)
                  .subtract(1, "day")
                  .format("DD/MM/YYYY")}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

const CampaignItemSkeleton = ({ isItemDisplay = true }) => {
  return (
    <div
      className={cn(
        "flex flex-col justify-between h-full group p-1 rounded-md ease-in duration-100",
        { "lg:flex-row": !isItemDisplay }
      )}
    >
      <div className={cn("relative", { "lg:flex-1": !isItemDisplay })}>
        <div className="w-full rounded-t-md aspect-square overflow-hidden">
          <Skeleton className="w-full rounded-t-md aspect-square" />
        </div>
        <div className="flex flex-col gap-1 py-2">
          <div>
            <div className="flex justify-between gap-2">
              <Skeleton className="w-10 h-4" />
            </div>
            <Skeleton className="w-full h-6 my-2" />
            <div className="flex-col flex gap-1">
              <div className="flex justify-between w-full">
                <Skeleton className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { CampaignItem, CampaignItemSkeleton }
